// /services/crypto/deviceKeyApi.ts

import baseApi from "../../lib/baseApi";

import {
  getPublicKeyPem,
} from "../../utils/crypto/keyStorageSecure";

import {
  ensureDeviceIdReady,
  getDeviceId,
} from "../../utils/common/deviceHelpers";

import { getDeviceIdAsync } from "../../utils/keyManager";
import { getInstallIdAsync } from "../../utils/common/installId";
import { getFingerprintHintHash } from "../../utils/common/fingerprintHint";
import { requestFcmToken } from "../../lib/firebase/firebaseClient";

import {
  shouldBlockPrivateIntegrationsNow,
} from "../../utils/security/privateIntegrationGate";

import {
  ConversationEncryptionResetResponse,
  ConversationEncryptionState,
  PopPayload,
  RegisterDeviceKeyResponse,
} from "../../types/crypto/cryptoTypes";

const blockedKeyFlowResponse = {
  skipped: true,
  reason: "private_integrations_blocked",
  pop: null,
};

const shouldSkipDeviceKeyRequest = (): boolean =>
  shouldBlockPrivateIntegrationsNow();

export const getFriendlyDeviceName = (): string => {
  if (typeof navigator === "undefined") {
    return "Server";
  }

  const navigation = navigator as Navigator & {
    userAgentData?: {
      brands?: Array<{
        brand: string;
        version: string;
      }>;
      platform?: string;
    };
  };

  const brands =
    navigation.userAgentData?.brands || [];

  const platform =
    navigation.userAgentData?.platform ||
    navigator.platform ||
    "Unknown";

  const selectedBrand =
    brands.find((item) =>
      /Chrome|Chromium|Edge|Safari|Firefox/i.test(
        item.brand,
      ),
    ) ||
    brands[0];

  if (selectedBrand) {
    const base = platform
      .replace(
        / Macintosh| Intel| x86_64/gi,
        "",
      )
      .trim();

    const version = selectedBrand.version
      .split(".")[0];

    const label =
      `${base} • ${selectedBrand.brand} ${version}`.trim();

    return label.length > 64
      ? label.slice(0, 64)
      : label;
  }

  const userAgent = navigator.userAgent || "";

  let browser = "Browser";
  let version = "";

  const chromeMatch =
    userAgent.match(/Chrome\/(\d+)/i);

  const edgeMatch =
    userAgent.match(/Edg\/(\d+)/i);

  const safariMatch =
    userAgent.match(/Version\/(\d+).*Safari/i);

  const firefoxMatch =
    userAgent.match(/Firefox\/(\d+)/i);

  if (edgeMatch) {
    browser = "Edge";
    version = edgeMatch[1];
  } else if (chromeMatch) {
    browser = "Chrome";
    version = chromeMatch[1];
  } else if (safariMatch) {
    browser = "Safari";
    version = safariMatch[1];
  } else if (firefoxMatch) {
    browser = "Firefox";
    version = firefoxMatch[1];
  }

  return `${navigator.platform || "Unknown"} • ${browser} ${version}`.trim();
};

// MARK: - Authoritative encryption state

export async function fetchConversationEncryptionState(): Promise<ConversationEncryptionState> {
  const response = await baseApi.get(
    "/accounts/conversation-encryption/state/",
  );

  return response.data;
}

// MARK: - Devices

export async function fetchUserDevices() {
  const response = await baseApi.get(
    "/accounts/auth/my-devices/",
  );

  return response.data;
}

export async function sendDeviceDeletionCode(
  deviceId: string,
) {
  await ensureDeviceIdReady();

  const target =
    deviceId.trim().toLowerCase();

  const response = await baseApi.post(
    "/accounts/auth/send-device-deletion-code/",
    {
      device_id: target,
    },
    {
      headers: {
        "X-Device-ID": target,
      },
    },
  );

  return response.data;
}

export async function verifyAndDeleteDevice(
  deviceId: string,
  code: string,
) {
  await ensureDeviceIdReady();

  const target =
    deviceId.trim().toLowerCase();

  const response = await baseApi.post(
    "/accounts/auth/verify-delete-device/",
    {
      device_id: target,
      code,
    },
    {
      headers: {
        "X-Device-ID": target,
      },
    },
  );

  return response.data;
}

// MARK: - Backup

export async function uploadKeyBackup(
  deviceId: string,
  blob: unknown,
): Promise<boolean> {
  const target =
    deviceId.trim().toLowerCase();

  await baseApi.post(
    "/accounts/auth/key-backup-save/",
    {
      device_id: target,
      blob,
    },
    {
      headers: {
        "X-Device-ID": target,
      },
    },
  );

  return true;
}

export async function fetchKeyBackup(
  deviceId: string,
) {
  const normalized =
    deviceId.trim().toLowerCase();

  const response = await baseApi.get(
    "/accounts/auth/key-backup-fetch/",
    {
      headers: {
        "X-Device-ID": normalized,
      },
      params: {
        device_id: normalized,
      },
    },
  );

  return response.data?.blob || null;
}

export async function hasKeyBackup(): Promise<boolean> {
  await ensureDeviceIdReady();

  const deviceId = getDeviceId();

  const response = await baseApi.get(
    "/accounts/auth/has-key-backup/",
    {
      headers: {
        "X-Device-ID": deviceId,
      },
      params: {
        device_id: deviceId,
      },
    },
  );

  return Boolean(
    response.data?.has_backup,
  );
}

export async function getPassphraseProfile(): Promise<{
  has_passphrase: boolean;
  kdf: string;
  iterations: number;
}> {
  if (shouldSkipDeviceKeyRequest()) {
    return {
      has_passphrase: false,
      kdf: "PBKDF2",
      iterations: 600_000,
    };
  }

  const response = await baseApi.get(
    "/accounts/auth/passphrase-profile/",
  );

  return response.data;
}

export async function markPassphraseSet(
  kdf = "PBKDF2",
  iterations = 600_000,
) {
  return baseApi.post(
    "/accounts/auth/passphrase-profile-set/",
    {
      kdf,
      iterations,
    },
  );
}

export async function hasAnyKeyBackup(): Promise<boolean> {
  if (shouldSkipDeviceKeyRequest()) {
    return false;
  }

  const response = await baseApi.get(
    "/accounts/auth/has-any-key-backup/",
  );

  return Boolean(
    response.data?.has_backup,
  );
}

export async function fetchLatestKeyBackup() {
  const response = await baseApi.get(
    "/accounts/auth/key-backup-latest/",
  );

  return response.data?.blob || null;
}

export async function listKeyBackups(): Promise<
  Array<{
    device_id: string;
    updated_at: string;
  }>
> {
  const response = await baseApi.get(
    "/accounts/auth/key-backup-list/",
  );

  return response.data?.items || [];
}

export async function backfillDeviceKeys(
  deviceId?: string,
) {
  if (shouldSkipDeviceKeyRequest()) {
    return blockedKeyFlowResponse;
  }

  await ensureDeviceIdReady();

  const normalizedDeviceId = (
    deviceId || getDeviceId()
  )
    .trim()
    .toLowerCase();

  const response = await baseApi.post(
    "/accounts/auth/backfill-device-keys/",
    {
      device_id: normalizedDeviceId,
    },
    {
      headers: {
        "X-Device-ID": normalizedDeviceId,
      },
    },
  );

  return response.data;
}

// MARK: - Registration

export async function registerCurrentPublicKey(
  allowRotate = false,
  replaceSameFingerprint = false,
): Promise<
  RegisterDeviceKeyResponse | typeof blockedKeyFlowResponse
> {
  if (shouldSkipDeviceKeyRequest()) {
    return blockedKeyFlowResponse;
  }

  const [
    deviceId,
    installId,
    fingerprintHint,
  ] = await Promise.all([
    getDeviceIdAsync(),
    getInstallIdAsync(),
    getFingerprintHintHash(),
  ]);

  if (shouldSkipDeviceKeyRequest()) {
    return blockedKeyFlowResponse;
  }

  const publicKey =
    await getPublicKeyPem();

  if (!publicKey) {
    throw new Error(
      "No local public encryption key was found.",
    );
  }

  const pushToken =
    await requestFcmToken();

  const response = await baseApi.post(
    "/accounts/auth/register-device-key/",
    {
      device_id: deviceId,
      public_key: publicKey,
      allow_rotate: allowRotate,
      replace_same_fp: replaceSameFingerprint,
      device_name: getFriendlyDeviceName(),
      install_id: installId,
      fingerprint_hint: fingerprintHint,
      push_token: pushToken,
      platform: "web",
    },
    {
      headers: {
        "X-Device-ID": deviceId,
        "X-Install-ID": installId,
      },
    },
  );

  return response.data;
}

// MARK: - Atomic encryption identity reset

export async function resetConversationEncryptionIdentity(
  accountPassword: string,
  confirmation: string,
  publicKey: string,
  deviceId: string,
): Promise<ConversationEncryptionResetResponse> {
  const [
    installId,
    fingerprintHint,
    pushToken,
  ] = await Promise.all([
    getInstallIdAsync(),
    getFingerprintHintHash(),
    requestFcmToken(),
  ]);

  const response = await baseApi.post(
    "/accounts/auth/reset-conversation-encryption/",
    {
      account_password: accountPassword,
      confirmation,
      device_id: deviceId,
      public_key: publicKey,
      device_name: getFriendlyDeviceName(),
      install_id: installId,
      fingerprint_hint: fingerprintHint,
      push_token: pushToken,
      platform: "web",
    },
    {
      headers: {
        "X-Device-ID": deviceId,
        "X-Install-ID": installId,
      },
    },
  );

  return response.data;
}

// MARK: - PoP

export async function requestPopChallenge(
  deviceId?: string,
): Promise<{
  pop: PopPayload | null;
  verified: boolean;
}> {
  if (shouldSkipDeviceKeyRequest()) {
    return {
      pop: null,
      verified: false,
    };
  }

  await ensureDeviceIdReady();

  const normalizedDeviceId = (
    deviceId || getDeviceId()
  )
    .trim()
    .toLowerCase();

  const response = await baseApi.post(
    "/accounts/auth/device-pop-challenge/",
    {
      device_id: normalizedDeviceId,
    },
    {
      headers: {
        "X-Device-ID": normalizedDeviceId,
      },
    },
  );

  return {
    pop: response.data?.pop || null,
    verified: Boolean(
      response.data?.verified,
    ),
  };
}

export async function verifyPop(
  deviceId?: string,
  nonceBase64?: string,
) {
  if (shouldSkipDeviceKeyRequest()) {
    return {
      skipped: true,
      verified: false,
      reason: "private_integrations_blocked",
    };
  }

  await ensureDeviceIdReady();

  const normalizedDeviceId = (
    deviceId || getDeviceId()
  )
    .trim()
    .toLowerCase();

  const response = await baseApi.post(
    "/accounts/auth/device-pop-verify/",
    {
      device_id: normalizedDeviceId,
      nonce_b64: nonceBase64,
    },
    {
      headers: {
        "X-Device-ID": normalizedDeviceId,
      },
    },
  );

  return response.data;
}